import useSessionStorage from "@postinumero/storage/useSessionStorage";
import {BooleanParam, useQueryParam} from 'use-query-params';
import {Button, Grid, Paper, Typography} from '@material-ui/core';
import {useHistory, useRouteMatch} from 'react-router-dom';
import {makeStyles} from '@material-ui/core/styles';
import {Alert} from '@material-ui/lab';
import BackButton from '@super-template/core/src/BackButton';
import messages from '@super-template/messages';
import {useHoliday} from 'api';
import {FormattedMessage} from 'react-intl';
import {isPast} from 'date-fns';
import Page from '../../layouts/Page';
import {getPrimary, getWishArray, savePrimary, saveWishArray} from '../utils';
import Content from './Content';
import {useContext, useEffect, useState} from 'react';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import ButtonGroup from "@material-ui/core/ButtonGroup";
import {SettingsContext} from "../application/utils";
import getTranslated from "@super-template/core/src/locale/getTranslated";
import useLocale from "@super-template/core/src/locale/useLocale";
import ReactHtmlParser from "react-html-parser";
import {sortBy} from 'lodash';

export const path = '/:organization/info/:id';
export const exact = true;

const useStyles = makeStyles((theme) => ({
  content: {
    order: 1,
    [theme.breakpoints.down('sm')]: {
      order: 2,
    },
  },
  info: {
    order: 2,
    [theme.breakpoints.down('sm')]: {
      order: 1,
    },
  },
  html: {
    '& p': {
      margin: 0
    }
  },
  buttons: {
    order: 3,
  },
  paper: {
    padding: theme.spacing(2),
    height: '100%',
  },
  disabled_info: {
    display: 'inline-flex',
    verticalAlign: 'middle',
    '& svg': {
      marginRight: 5,
    },
  },
  buttonGroup: {
    [theme.breakpoints.down('sm')]: {
      flexFlow: 'wrap',
      '& >*': {
        marginBottom: theme.spacing(2)
      }
    },
  }
}));

export const component = function Info() {
  const classes = useStyles();
  const [locale] = useLocale();
  const history = useHistory();
  const {params: {organization, id}} = useRouteMatch();
  const [direct] = useQueryParam('direct', BooleanParam);
  const [, setFamilyHoliday] = useSessionStorage('familyHoliday');
  const {organization: {isHolidayOrganization}, infoTexts} = useContext(SettingsContext);
  const search = history.location.state?.toString();
  const info = useHoliday({id, organization});
  const [wishArray, setWishArray] = useState(getWishArray());
  const [isWish, setIsWish] = useState();
  const [primary] = useState(getPrimary());
  const title = isHolidayOrganization ? messages.holiday_info_title : messages.course_info_title;
  const past = isPast(new Date(info.applicationEndDate + "T23:59:59"));
  const full = wishArray.length >= 3;

  useEffect(() => {
    setIsWish(wishArray.filter((item) => item.id === parseInt(id)).length > 0);
  }, [wishArray, id]);

  const apply = () => {
    saveWishArray([...wishArray, info]);
    if (direct) setFamilyHoliday(info.familyHoliday.toString());
    history.push({pathname: `/${organization}`, search, state: {wish_added: true}});
  };

  const remove = () => {
    const filtered = wishArray.filter((item) => parseInt(item.id) !== parseInt(id));
    if (parseInt(primary.id) === parseInt(id)) {
      if (filtered.length > 0) savePrimary(filtered[0]);
      else savePrimary(undefined);
    }
    setWishArray(saveWishArray(filtered));
    history.push({pathname: `/${organization}`, search, state: {wish_removed: true}});
  };

  return (
    <Page title={<FormattedMessage {...title} />}>
      {info ?
        <Grid container spacing={3}>
          <Grid item xs={12} md={4} className={classes.info}>
            <Paper className={classes.paper}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="h5" component="h2" gutterBottom>
                    <FormattedMessage {...title} />
                  </Typography>
                  <div className={classes.html}>
                    {ReactHtmlParser(getTranslated(infoTexts.INFO?.text, locale))}
                  </div>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
          <Grid item xs={12} md={8} className={classes.content}>
            <Paper className={classes.paper}>
              <Content main info={info} isHolidayOrganization={isHolidayOrganization} organization={organization}
                       locale={locale}/>
              {info.nextPeriod && sortBy(info.nextPeriod, 'startDate').map((i, index) =>
                <div key={'period_' + index}>
                  <hr/>
                  <Content info={i} isHolidayOrganization={isHolidayOrganization} num={index}
                           organization={organization} locale={locale}/>
                </div>)}
            </Paper>
          </Grid>
          <Grid item xs={12} className={classes.buttons}>
            <ButtonGroup className={classes.buttonGroup}>
              <BackButton variant="outlined" color={'primary'}/>
              {isWish ?
                <Button variant="contained" color="primary" onClick={remove}>
                  <FormattedMessage {...messages.remove_wish} />
                </Button> :
                <Button disabled={past || full} variant="contained" color={'primary'} onClick={apply}>
                  <FormattedMessage {...isHolidayOrganization ? messages.add_wish : messages.add_course} />
                </Button>}
            </ButtonGroup>
            {((past || full) && !isWish) &&
            <Typography variant={"body1"} className={classes.disabled_info}>
              <InfoOutlinedIcon color={'primary'}/>
              {full ?
                <FormattedMessage {...isHolidayOrganization ? messages.wishes_full : messages.wishes_full_c} /> :
                <FormattedMessage {...messages.application_ended} />}
            </Typography>}
          </Grid>
        </Grid> :
        <Alert severity="warning">
          <FormattedMessage {...messages.holiday_not_found} />
        </Alert>}
    </Page>
  );
};
